import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  type MailAccount,
  type MailTestResult,
} from "@/api/client";
import { useAuth } from "@/auth/useAuth";
import Toasts from "@/components/Toasts";
import SessionGuard from "@/components/SessionGuard";
import { toast } from "@/lib/toast";
import {
  ArrowLeft,
  Mail,
  Loader2,
  KeyRound,
  Pencil,
  PlugZap,
  Save,
  PauseCircle,
  PlayCircle,
  Trash2,
  AlertTriangle,
  Info,
  CheckCircle2,
  ListChecks,
  Paperclip,
} from "lucide-react";

// 개인 메일 연동 — 본인 회사 메일(IMAP)을 ISAC 지식저장소에 저장하도록 신청·설정하는 화면.
// 설계: ISAC_개인메일연동_구현계획_v0.6.md §5-1 / API 는 포털 BFF /api/chat/mail/*.
//
// ⚠ 이 화면은 "본인 전용" 저장만 다룬다(공유 폴더 기능은 범위 밖). 동의 문구도 공유
//   관련 조항을 제외한 본문만 표시한다.

/** 동의 문구 버전 기본값 — §12 문구 v2 기준.
 *  실제 판정은 서버 `GET /mail/status` 의 consent_version 을 우선 사용한다
 *  (서버 PERSONAL_MAIL_CONSENT_VERSION 이 단일 출처). */
const CONSENT_VERSION_FALLBACK = 2;

const SINCE_OPTIONS = [
  { v: 1, label: "최근 1개월" },
  { v: 3, label: "최근 3개월" },
  { v: 6, label: "최근 6개월" },
  { v: 12, label: "최근 12개월" },
];

const RETENTION_OPTIONS = [
  { v: 0, label: "무제한" },
  { v: 6, label: "6개월" },
  { v: 12, label: "12개월" },
  { v: 24, label: "24개월" },
];

/** 동의 문구 1항 첫 줄 — 첨부 저장 옵트인 기능(서버 attach_optin)이 켜지면 옵트인판으로 바꾼다.
 *  2026-09-30 결정: 수집 범위가 **줄어드는** 변경이라 문구만 고치고 동의 버전(v2)은 올리지 않는다
 *  (올리면 기존 연동자 전원이 재동의 전까지 수집이 멈춘다). */
const CONSENT_COLLECT_ALWAYS =
  "NAVER WORKS 회사 메일 계정의 받은편지함과 보낸편지함에 있는 메일의 제목, 본문, 보낸 사람·받는 사람, 일시, 첨부파일(문서류에 한함. 동영상·실행파일 제외)은 본인 전용으로 저장됩니다.";
const CONSENT_COLLECT_OPTIN =
  "NAVER WORKS 회사 메일 계정의 받은편지함과 보낸편지함에 있는 메일의 제목, 본문, 보낸 사람·받는 사람, 일시는 본인 전용으로 저장됩니다. 첨부파일은 파일명과 첨부 여부만 기록하며, 본인이 「첨부파일도 저장」을 켠 경우에만 문서류 파일(동영상·실행파일·이미지 제외)을 본인 전용으로 저장합니다. 이 설정은 켠 뒤 새로 수집되는 메일부터 적용되고, 끄더라도 이미 저장된 첨부파일은 삭제되지 않습니다.";

/** 동의 문구 전문(§12, 공유 관련 조항 제외). 소제목 + 항목 목록. 1항 첫 줄은 위 상수로 바꿔 끼운다. */
const CONSENT_SECTIONS: { title: string; items: string[] }[] = [
  {
    title: "1. 수집하는 정보",
    items: [
      CONSENT_COLLECT_ALWAYS,
      "수집 범위는 본인이 선택한 과거 기간의 메일부터 시작하며, 이후 새로 도착·발송되는 메일이 매일 1회 반영됩니다.",
      "전 직원 대상 안내 메일(사내 소식, 뉴스클리핑, 교육 공지 등 회사가 정한 제목 패턴)과 본인이 지정한 제외 규칙에 해당하는 메일은 수집하지 않습니다.",
      "연동을 위해 입력한 외부 앱 비밀번호는 암호화되어 서버에만 저장되며, 화면이나 로그에 다시 표시되지 않습니다. 회사 메일의 로그인 비밀번호는 입력하지 마세요.",
    ],
  },
  {
    title: "2. 저장 위치와 열람 범위",
    items: [
      "저장된 메일은 지식저장소의 본인 전용 책(개인메일_본인이메일)과 ISAC 검색 색인의 본인 전용 영역에 저장됩니다. 본인과 시스템 관리자만 열 수 있고, 다른 직원의 질문에는 사용되지 않습니다.",
      "시스템 관리자는 장애 대응·보안 점검 목적에 한해 접근할 수 있으며, 그 외 목적으로 열람하지 않습니다. 관리자의 접근 행위는 모두 기록되어 3년간 보관됩니다.",
    ],
  },
  {
    title: "3. 보존과 삭제",
    items: [
      "보존 기간은 본인이 선택합니다(무제한 또는 6·12·24개월). 기간이 지난 메일은 자동으로 삭제됩니다.",
      "언제든지 「내 메일 연동」 화면에서 일시 중지 또는 연동 해제할 수 있습니다. 연동 해제 시 저장된 메일의 원문·검색 색인·전용 책은 모두 삭제되며 복구되지 않습니다.",
      "퇴직하거나 다른 부서로 전보되면 연동이 자동으로 해제되고 저장된 메일은 모두 삭제됩니다. 전보 후에도 계속 이용하려면 새 부서에서 다시 연동을 신청해 주세요.",
    ],
  },
  {
    title: "4. 주의 사항",
    items: [
      "사적 용도의 메일이나 외부 개인 계정 메일은 연동 대상이 아닙니다. 필요하면 제외 규칙으로 걸러 주세요.",
      "본 기능은 업무 지식 축적을 돕기 위한 것이며, 근무 평가나 감사 목적으로 사용되지 않습니다.",
    ],
  },
  {
    // 메일 업무비서(선택) — 2026-09-22 결정: 기능 공지 전이라 문구 버전은 v2 그대로 두고 조항만 추가.
    title: "5. 메일 업무비서 (선택)",
    items: [
      "「메일 업무비서」를 켜면, 저장된 본인 메일에서 AI(Anthropic Claude)가 해야 할 일·기한·요청 유형·고객사·요청자를 추출해 본인 전용 업무 목록을 만듭니다. 추출을 위해 메일 본문 일부가 AI 모델에 일시 전송되며, 주민등록번호·카드번호는 전송 전에 가려지고 전송 내용은 모델 학습에 사용되지 않습니다.",
      "기한이 임박하면 NAVER WORKS 메신저로 알림을 보낼 수 있습니다. 알림은 「내 업무」 화면에서 끌 수 있습니다.",
      "업무 목록은 본인만 볼 수 있습니다. 관리자는 개인별 업무 건수·업무명을 볼 수 없으며, 연동 인원·시스템 오류·이용량 통계만 봅니다.",
      "AI 가 기한이나 업무 내용을 잘못 읽을 수 있습니다. 알림은 참고용이며 최종 확인은 메일함에서 해 주세요. 잘못된 항목은 화면에서 직접 고칠 수 있습니다.",
      "업무비서만 끄면 추출된 업무 목록은 삭제되고 저장된 메일은 유지됩니다. 연동 해제 시에는 업무 목록도 함께 삭제됩니다.",
    ],
  },
  {
    title: "6. 문의",
    items: ["기능·보안 관련 문의: CSD팀 윤주열 수석부장 (leoyun@insunginfo.co.kr)"],
  },
];

/** 공유는 이 화면의 기능이 아니므로, 공유가 필요할 때의 대안을 마지막에 한 줄로 안내한다. */
const CONSENT_FOOTNOTE =
  "업무 지식을 동료와 공유하려면 해당 메일을 수집 주소로 전달하거나 참조에 넣으세요. 이 화면의 저장 범위는 본인만 검색합니다.";

/** textarea 문자열 ↔ 문자열 배열 변환(한 줄에 하나, 빈 줄 제거). */
function toLines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function fromLines(items: string[] | undefined): string {
  return (items ?? []).join("\n");
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function StatusBadge({ account }: { account: MailAccount }) {
  if (account.paused)
    return (
      <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
        일시 중지
      </span>
    );
  if (account.status === "ok")
    return (
      <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
        정상
      </span>
    );
  if (account.status === "auth_error")
    return (
      <span className="rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] text-red-700">
        인증 오류
      </span>
    );
  if (account.status === "bs_user_missing")
    return (
      <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
        저장소 계정 확인 필요
      </span>
    );
  return (
    <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
      아직 동기화 안 됨
    </span>
  );
}

const CARD = "rounded-xl border border-border bg-card p-3";
const LABEL = "mb-1.5 block text-xs font-semibold text-foreground";
const HELP = "mt-1 text-[11px] leading-relaxed text-muted-foreground";
const INPUT =
  "w-full rounded-lg border border-border bg-background px-2.5 py-2 text-sm outline-none focus:border-primary";
const TEXTAREA = INPUT + " min-h-[72px] font-mono text-xs";
const BTN =
  "flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-40";
const BTN_PRIMARY =
  "flex items-center justify-center gap-1.5 rounded-lg bg-[#EB4E26] px-3 py-2 text-sm font-semibold text-white disabled:opacity-40";
const CHECK = "flex cursor-pointer items-center gap-1.5 text-sm";

export default function MailLink() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  // 메일 업무비서(mail_task) — 서버 기능 on/off 와 계정 옵트인
  const [taskFeature, setTaskFeature] = useState(false);
  const [taskEnabled, setTaskEnabled] = useState(false);
  // 첨부파일 저장 옵트인 (2026-09-30) — 서버 기능 on/off 와 계정 설정(기본 저장 안 함)
  const [attachOptin, setAttachOptin] = useState(false);
  const [attachEnabled, setAttachEnabled] = useState(false);
  // 서버가 요구하는 동의 문구 버전 — 개정되면 재동의를 받는다.
  // ref 를 함께 두는 이유: applyAccount 가 이 값을 읽는데, 의존성에 넣으면 최초 로드
  // useEffect 가 한 번 더 돌아 불필요한 재조회가 발생한다(값은 status 응답에서 확정된다).
  const [consentVersion, setConsentVersion] = useState(CONSENT_VERSION_FALLBACK);
  const consentVersionRef = useRef(CONSENT_VERSION_FALLBACK);
  const [account, setAccount] = useState<MailAccount | null>(null);

  // 비밀번호 — 저장된 값은 절대 내려오지 않으므로(has_secret 만), 변경할 때만 입력칸을 연다.
  const [secret, setSecret] = useState("");
  const [editSecret, setEditSecret] = useState(true);

  // 수집 범위
  const [inbox, setInbox] = useState(true);
  const [sent, setSent] = useState(true);
  const [sinceMonths, setSinceMonths] = useState(6);
  const [retentionMonths, setRetentionMonths] = useState(0);

  // 개인 제외 규칙(한 줄에 하나)
  const [exSubject, setExSubject] = useState("");
  const [exFrom, setExFrom] = useState("");
  const [exKeywords, setExKeywords] = useState("");

  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<MailTestResult | null>(null);
  // 연동 해제 2단계 확인 — 모달에서 "삭제" 를 직접 입력해야 버튼이 열린다.
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [unlinkText, setUnlinkText] = useState("");
  const [unlinking, setUnlinking] = useState(false);

  /** 서버 값으로 폼 채우기. */
  const applyAccount = useCallback((acc: MailAccount | null) => {
    setAccount(acc);
    if (!acc) {
      setEditSecret(true);
      return;
    }
    setInbox(acc.folders?.inbox ?? true);
    setSent(acc.folders?.sent ?? true);
    setSinceMonths(acc.since_months || 6);
    setRetentionMonths(acc.retention_months ?? 0);
    setExSubject(fromLines(acc.exclude?.subject));
    setExFrom(fromLines(acc.exclude?.from));
    setExKeywords(fromLines(acc.exclude?.keywords));
    setTaskEnabled(!!acc.task_enabled);
    setAttachEnabled(!!acc.attach_enabled);
    // 문구가 개정되면(버전 상향) 다시 동의를 받는다.
    setConsent((acc.consent_version ?? 0) >= consentVersionRef.current);
    setEditSecret(!acc.has_secret);
    setSecret("");
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const st = await api.mailStatus();
        if (!alive) return;
        setEnabled(!!st.enabled);
        setTaskFeature(!!st.enabled && !!st.task_feature);
        setAttachOptin(!!st.enabled && !!st.attach_optin);
        if (st.consent_version && st.consent_version > 0) {
          consentVersionRef.current = st.consent_version;
          setConsentVersion(st.consent_version);
        }
        if (!st.enabled) return;
        const r = await api.mailAccount();
        if (!alive) return;
        applyAccount(r.account ?? null);
      } catch (e) {
        if (!alive) return;
        setEnabled(false);
        toast((e as Error).message || "메일 연동 정보를 불러오지 못했습니다.", "error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [applyAccount]);

  const payload = (paused: boolean) => ({
    secret: editSecret && secret ? secret : null, // null = 기존 비밀번호 유지
    folders: { inbox, sent },
    since_months: sinceMonths,
    retention_months: retentionMonths,
    exclude: {
      subject: toLines(exSubject),
      from: toLines(exFrom),
      keywords: toLines(exKeywords),
    },
    consent_version: consentVersion,
    paused,
    task_enabled: taskFeature && taskEnabled,
    // 옵트인 기능이 꺼져 있으면 null(= 서버 기존 값 유지) — 기능 off 에서는 설정을 건드리지 않는다
    attach_enabled: attachOptin ? attachEnabled : null,
  });

  const save = async (paused: boolean, okMsg: string) => {
    if (!consent) {
      toast("동의 확인이 필요합니다.", "error");
      return;
    }
    if (!account?.has_secret && !secret) {
      toast("외부 앱 비밀번호를 입력해 주세요.", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await api.mailSave(payload(paused));
      applyAccount(r.account ?? null);
      setTestResult(null);
      toast(okMsg, "success");
    } catch (e) {
      toast((e as Error).message || "저장에 실패했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.mailTest(editSecret && secret ? secret : null);
      setTestResult(r);
      if (r.ok) toast("연결에 성공했습니다.", "success");
      else toast(`연결 실패: ${r.error_code || "알 수 없는 오류"}`, "error");
    } catch (e) {
      toast((e as Error).message || "연결 테스트에 실패했습니다.", "error");
    } finally {
      setTesting(false);
    }
  };

  const doUnlink = async () => {
    setUnlinking(true);
    try {
      await api.mailUnlink();
      applyAccount(null);
      setUnlinkOpen(false);
      setUnlinkText("");
      toast("연동을 해제하고 저장된 메일을 모두 삭제했습니다.", "success");
    } catch (e) {
      toast((e as Error).message || "연동 해제에 실패했습니다.", "error");
    } finally {
      setUnlinking(false);
    }
  };

  // ── 로딩 / 기능 비활성 ──
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> 불러오는 중…
      </div>
    );
  }

  const header = (
    <header className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background/90 px-2 py-2 backdrop-blur" style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}>
      <button
        onClick={() => navigate("/")}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-muted"
        aria-label="채팅으로 돌아가기"
        title="채팅으로 돌아가기"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <div className="flex items-center gap-1.5 font-bold">
        <Mail className="h-5 w-5 text-[#EB4E26]" />
        내 메일 연동
      </div>
      <span className="ml-2 hidden text-xs text-muted-foreground sm:inline">
        내 회사 메일을 나만 검색할 수 있게 저장합니다
      </span>
    </header>
  );

  if (enabled === false) {
    return (
      <div className="flex h-full flex-col">
        <SessionGuard />
        {header}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-3 py-6">
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <Info className="mx-auto h-7 w-7 text-muted-foreground" />
              <div className="mt-2 text-sm font-medium">관리자가 기능을 활성화하지 않았습니다</div>
              <p className="mt-1 text-xs text-muted-foreground">
                개인 메일 연동은 현재 사용할 수 없습니다. 필요하시면 관리자에게 문의해 주세요.
              </p>
            </div>
          </div>
        </main>
        <Toasts />
      </div>
    );
  }

  const linked = !!account;
  const canSave = consent && (account?.has_secret || !!secret);
  // 연결 테스트는 동의 없이도 가능 — 저장된 비밀번호가 있거나 새로 입력한 경우.
  const canTest = !!account?.has_secret || !!secret;

  return (
    <div className="flex h-full flex-col">
      <SessionGuard />

      {/* 연동 해제 2단계 확인 — 확인 문구를 직접 입력해야 삭제 버튼이 열린다. */}
      {unlinkOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !unlinking && setUnlinkOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center gap-1.5 font-semibold text-red-600">
              <AlertTriangle className="h-4 w-4" /> 연동 해제 + 데이터 삭제
            </div>
            <p className="text-sm text-muted-foreground">
              저장된 메일의 원문·검색 색인·전용 책이 <b className="text-foreground">모두 삭제</b>되며
              복구되지 않습니다. 계속하려면 아래에 <b className="text-foreground">삭제</b> 를 입력해
              주세요.
            </p>
            <input
              value={unlinkText}
              onChange={(e) => setUnlinkText(e.target.value)}
              placeholder="삭제"
              className={INPUT + " mt-3"}
              aria-label="확인 문구 입력"
            />
            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={doUnlink}
                disabled={unlinkText.trim() !== "삭제" || unlinking}
                className="w-full rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                {unlinking ? "삭제 중…" : "연동 해제하고 모두 삭제"}
              </button>
              <button
                onClick={() => setUnlinkOpen(false)}
                disabled={unlinking}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {header}

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl space-y-3 px-3 py-4">
          {/* 상태 배너 — 인증 오류 / 지식저장소 계정 없음 */}
          {account?.status === "auth_error" && (
            <div className="flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">앱 비밀번호를 다시 발급해 주세요</div>
                <div className="mt-0.5 text-xs">
                  저장된 비밀번호로 메일 서버에 로그인할 수 없습니다. 새 외부 앱 비밀번호를 발급해
                  아래에서 변경해 주세요.
                </div>
              </div>
            </div>
          )}
          {account?.status === "bs_user_missing" && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">지식저장소에 한 번 로그인해 주세요</div>
                <div className="mt-0.5 text-xs">
                  kms.insunginfo.co.kr 에 최초 1회 로그인하면 본인 전용 책을 만들 수 있습니다.
                </div>
              </div>
            </div>
          )}

          {/* 상태 영역 */}
          <div className={CARD}>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold">연동 상태</div>
              {linked ? (
                <StatusBadge account={account!} />
              ) : (
                <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  미연동
                </span>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">계정</dt>
                <dd className="mt-0.5 truncate font-medium" title={user?.email ?? ""}>
                  {user?.email ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">마지막 동기화</dt>
                <dd className="mt-0.5 font-medium tabular-nums">
                  {fmtDate(account?.last_sync_at)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">누적 스레드</dt>
                <dd className="mt-0.5 font-medium tabular-nums">
                  {(account?.thread_count ?? 0).toLocaleString("ko-KR")}건
                </dd>
              </div>
            </dl>
            {account?.last_error && (
              <div className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700">
                마지막 오류: {account.last_error}
              </div>
            )}
          </div>

          {/* 외부 앱 비밀번호 */}
          <div className={CARD}>
            <label className={LABEL}>
              <KeyRound className="mr-1 inline h-3.5 w-3.5" />
              외부 앱 비밀번호
            </label>
            {account?.has_secret && !editSecret ? (
              <div className="flex items-center gap-2">
                <span className="flex-1 rounded-lg border border-border bg-muted px-2.5 py-2 text-sm tracking-widest text-muted-foreground">
                  ●●●●●● (저장됨)
                </span>
                <button onClick={() => setEditSecret(true)} className={BTN}>
                  <Pencil className="h-3.5 w-3.5" /> 변경
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="new-password"
                  placeholder="외부 앱 비밀번호"
                  className={INPUT + " flex-1"}
                />
                {account?.has_secret && (
                  <button
                    onClick={() => {
                      setEditSecret(false);
                      setSecret("");
                    }}
                    className={BTN}
                  >
                    취소
                  </button>
                )}
              </div>
            )}
            <p className={HELP}>
              네이버웍스 설정 › 보안 › 외부 앱 비밀번호에서 발급하세요. 로그인 비밀번호가 아닙니다.
            </p>
          </div>

          {/* 수집 범위 */}
          <div className={CARD}>
            <div className={LABEL}>수집 범위</div>
            <div className="flex flex-wrap items-center gap-4">
              <label className={CHECK}>
                <input
                  type="checkbox"
                  checked={inbox}
                  onChange={(e) => setInbox(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                받은편지함
              </label>
              <label className={CHECK}>
                <input
                  type="checkbox"
                  checked={sent}
                  onChange={(e) => setSent(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                보낸편지함
              </label>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">
                  과거 메일 수집 기간
                </span>
                <select
                  value={sinceMonths}
                  onChange={(e) => setSinceMonths(Number(e.target.value))}
                  className={INPUT}
                >
                  {SINCE_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">보존 기간</span>
                <select
                  value={retentionMonths}
                  onChange={(e) => setRetentionMonths(Number(e.target.value))}
                  className={INPUT}
                >
                  {RETENTION_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className={HELP}>
              선택한 기간보다 오래된 메일은 저장하지 않고, 보존 기간이 지난 메일은 자동으로
              삭제됩니다.
            </p>
          </div>

          {/* 개인 제외 규칙 */}
          <div className={CARD}>
            <div className={LABEL}>개인 제외 규칙</div>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">제목 패턴</span>
                <textarea
                  value={exSubject}
                  onChange={(e) => setExSubject(e.target.value)}
                  placeholder={"[사내소식]\n뉴스클리핑"}
                  className={TEXTAREA}
                />
                <p className={HELP}>한 줄에 하나씩. 제목에 이 문구가 있으면 수집하지 않습니다.</p>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">보낸사람</span>
                <textarea
                  value={exFrom}
                  onChange={(e) => setExFrom(e.target.value)}
                  placeholder={"noreply@example.com\n@newsletter.co.kr"}
                  className={TEXTAREA}
                />
                <p className={HELP}>한 줄에 하나씩. 이 주소에서 온 메일은 수집하지 않습니다.</p>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">저장 차단 단어</span>
                <textarea
                  value={exKeywords}
                  onChange={(e) => setExKeywords(e.target.value)}
                  placeholder={"연봉\n급여\n인사평가\n진단서"}
                  className={TEXTAREA}
                />
                <p className={HELP}>
                  제목이나 본문에 이 단어가 있는 메일은 저장하지 않습니다. 예: 연봉, 급여, 인사평가,
                  진단서
                </p>
              </label>
            </div>
          </div>

          {/* 첨부파일 저장 옵트인 — 서버 기능이 켜져 있을 때만 노출 (2026-09-30, 기본 저장 안 함) */}
          {attachOptin && (
            <div className={CARD}>
              <label className={LABEL + " mb-0"}>
                <Paperclip className="mr-1 inline h-3.5 w-3.5" />
                첨부파일 저장 (선택)
              </label>
              <label className={CHECK + " mt-2"}>
                <input
                  type="checkbox"
                  checked={attachEnabled}
                  onChange={(e) => setAttachEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                첨부파일도 저장한다 (문서류만 · 동영상·실행파일·이미지 제외)
              </label>
              <p className={HELP}>
                끄면 첨부파일은 저장하지 않고 파일명과 첨부 여부만 기록합니다(기본). 켜면 저장한 뒤
                새로 수집되는 메일의 첨부부터 지식저장소 내 전용 책에 함께 저장되며, 과거 메일의
                첨부는 소급하지 않습니다. 다시 꺼도 이미 저장된 첨부파일은 삭제되지 않습니다.
              </p>
            </div>
          )}

          {/* 메일 업무비서 옵트인 — 서버 기능이 켜져 있을 때만 노출 */}
          {taskFeature && (
            <div className={CARD}>
              <div className="flex items-center justify-between">
                <label className={LABEL + " mb-0"}>
                  <ListChecks className="mr-1 inline h-3.5 w-3.5" />
                  메일 업무비서 (선택)
                </label>
                {linked && account?.task_enabled && (
                  <button onClick={() => navigate("/tasks")} className={BTN + " py-1 text-xs"}>
                    내 업무 보기
                  </button>
                )}
              </div>
              <label className={CHECK + " mt-2"}>
                <input
                  type="checkbox"
                  checked={taskEnabled}
                  onChange={(e) => setTaskEnabled(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                저장된 내 메일에서 할 일·기한·요청 유형을 자동으로 추출한다
              </label>
              <p className={HELP}>
                추출은 메일 수집 뒤 별도 주기로 돌고, 기한이 임박하면 NAVER WORKS 알림을 받을 수
                있습니다(「내 업무」에서 끄기 가능). 업무 목록은 본인만 봅니다. 동의 문구 5항을
                확인해 주세요. 체크를 끄고 저장하면 추출된 업무 목록은 삭제되고 메일 저장은
                유지됩니다.
              </p>
            </div>
          )}

          {/* 동의 */}
          <div className={CARD}>
            <div className={LABEL}>개인 메일 연동 동의</div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-muted-foreground">
              <p className="mb-2 text-foreground">
                ISAC 개인 메일 연동은 본인이 신청한 경우에만 본인의 회사 메일을 ISAC 지식저장소에
                저장하는 기능입니다. 저장된 메일은 본인의 질문에만 사용됩니다. 연동 전 아래 내용을
                확인해 주세요.
              </p>
              {CONSENT_SECTIONS.map((sec) => (
                <div key={sec.title} className="mb-2">
                  <div className="font-semibold text-foreground">{sec.title}</div>
                  <ul className="mt-0.5 list-disc space-y-0.5 pl-4">
                    {sec.items.map((it, i) => (
                      <li key={i}>
                        {/* 옵트인 기능이 켜지면 1항 첫 줄을 옵트인판 문구로 (버전 유지, 2026-09-30) */}
                        {attachOptin && it === CONSENT_COLLECT_ALWAYS ? CONSENT_COLLECT_OPTIN : it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <p className="mt-2 border-t border-border pt-2">{CONSENT_FOOTNOTE}</p>
            </div>
            <label className={CHECK + " mt-2.5"}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              위 내용을 확인했으며 동의합니다
            </label>
            {account && (account.consent_version ?? 0) < consentVersion && (
              <p className="mt-1 text-[11px] text-amber-700">
                동의 문구가 개정되었습니다. 다시 동의해 주세요.
              </p>
            )}
            {account?.consent_at && (
              <p className={HELP}>
                동의 일시 {fmtDate(account.consent_at)} · 문구 버전 v{account.consent_version}
              </p>
            )}
          </div>

          {/* 연결 테스트 결과 */}
          {testResult && (
            <div
              className={
                "rounded-xl border p-3 text-xs " +
                (testResult.ok
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-red-300 bg-red-50 text-red-700")
              }
            >
              <div className="flex items-center gap-1.5 font-semibold">
                {testResult.ok ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                {testResult.ok ? "연결 성공" : `연결 실패 (${testResult.error_code || "unknown"})`}
              </div>
              {testResult.ok && testResult.folders?.length > 0 && (
                <div className="mt-1">확인된 폴더: {testResult.folders.join(", ")}</div>
              )}
            </div>
          )}

          {/* 동작 버튼 */}
          <div className="flex flex-wrap items-center gap-2 pb-6">
            <button
              onClick={runTest}
              disabled={testing || !canTest}
              className={BTN}
              title={
                canTest
                  ? "저장하지 않고 메일 서버 접속만 확인합니다"
                  : "외부 앱 비밀번호를 입력하면 저장 전에도 확인할 수 있습니다"
              }
            >
              {testing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <PlugZap className="h-3.5 w-3.5" />
              )}
              연결 테스트
            </button>
            <button
              onClick={() => save(account?.paused ?? false, "저장했습니다.")}
              disabled={saving || !canSave}
              className={BTN_PRIMARY}
              title={consent ? "" : "동의 확인 후 저장할 수 있습니다"}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              저장·연동 시작
            </button>
            {linked &&
              (account!.paused ? (
                <button
                  onClick={() => save(false, "동기화를 재시작했습니다.")}
                  disabled={saving || !canSave}
                  className={BTN}
                >
                  <PlayCircle className="h-3.5 w-3.5" /> 재시작
                </button>
              ) : (
                <button
                  onClick={() => save(true, "동기화를 일시 중지했습니다.")}
                  disabled={saving || !canSave}
                  className={BTN}
                >
                  <PauseCircle className="h-3.5 w-3.5" /> 일시 중지
                </button>
              ))}
            {linked && (
              <button
                onClick={() => {
                  setUnlinkText("");
                  setUnlinkOpen(true);
                }}
                className="ml-auto flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" /> 연동 해제 + 데이터 삭제
              </button>
            )}
          </div>
        </div>
      </main>
      <Toasts />
    </div>
  );
}
